/* eslint eslint-comments/no-use: off */

import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {type ExtendedItemProps, ItemPicker, type ItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import {getQueryClient} from '@github-ui/query-client'
import {
  RepositoryPickerSearchRepositoriesServerQuery,
  RepositoryPickerTopRepositoriesServerQuery,
} from '@github-ui/server-persisted-queries'
import type {RepositoryPickerSearchRepositoriesServerQueryQuery} from '@github-ui/server-persisted-queries/types/RepositoryPickerSearchRepositoriesServerQuery'
import type {RepositoryPickerTopRepositoriesServerQueryQuery} from '@github-ui/server-persisted-queries/types/RepositoryPickerTopRepositoriesServerQuery'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {LockIcon, RepoIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, type SelectPanelProps} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import {
  getRepositorySearchQuery,
  type RepositoryPickerOwner as RepositoryPickerOwnerType,
} from '../utils/get-repository-search-query'
import {
  fetchPossibleTransferRepositories,
  type IssueTransferContext,
  possibleTransferRepositoriesQueryKey,
} from './fetch-possible-transfer-repositories'
import {
  fetchRepositoryFromServer as fetchRepositoryFromServerQuery,
  repositoryQueryKey as repositoryQueryKeyFromServer,
} from './fetch-repository'
import type {
  RepositoryPickerRepository as RepositoryPickerRepositoryType,
  RepositoryVisibility as RepositoryVisibilityType,
} from './repository-picker-types'
import styles from './RepositoryPicker.module.css'
import {SharedRequestManager} from './shared-request-manager'

export type RepositoryPickerRepository = RepositoryPickerRepositoryType
export type RepositoryVisibility = RepositoryVisibilityType
export type RepositoryPickerOwner = RepositoryPickerOwnerType

export type RepositoryPickerTopRepositoriesViewer = NonNullable<
  RepositoryPickerTopRepositoriesServerQueryQuery['viewer']
>

const OWNER_REPOSITORY_PRELOAD_MAX_PAGES = 5

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

async function fetchTopRepositoriesFromServer(
  options: {
    first?: number
    hasIssuesEnabled?: boolean
    owner?: string
  },
  signal?: AbortSignal,
): Promise<RepositoryPickerRepository[]> {
  const {first = 10, hasIssuesEnabled, owner} = options
  const query = new URLSearchParams({
    queryId: RepositoryPickerTopRepositoriesServerQuery,
    topRepositoriesFirst: String(first),
  })
  if (hasIssuesEnabled != null) query.set('hasIssuesEnabled', String(hasIssuesEnabled))
  if (owner != null) query.set('owner', owner)

  const response = await reactFetchJSON(`/_ghui/item-pickers/top_repositories?${query.toString()}`, {signal})
  if (!response.ok) throw new Error(`Top repositories fetch failed (${response.status})`)

  const json = (await response.json()) as {data?: RepositoryPickerTopRepositoriesServerQueryQuery; errors?: unknown[]}
  if (!json.data?.viewer || !json.data.viewer.topRepositories) {
    throw new Error('Top repositories query returned no data')
  }

  const edges = json.data.viewer.topRepositories.edges
  if (edges !== null && !Array.isArray(edges)) {
    throw new Error('Top repositories query returned invalid data')
  }

  return (edges ?? []).flatMap(edge => (edge?.node ? [edge.node as RepositoryPickerRepository] : []))
}

function isValidSearchConnection(value: unknown): value is {
  repositoryCount: number
  pageInfo: {hasNextPage: boolean; endCursor: string | null}
  nodes: unknown[] | null
} {
  if (!value || typeof value !== 'object') return false

  const candidate = value as {
    repositoryCount?: unknown
    pageInfo?: unknown
    nodes?: unknown
  }

  if (typeof candidate.repositoryCount !== 'number') return false
  if (!candidate.pageInfo || typeof candidate.pageInfo !== 'object') return false

  const pageInfo = candidate.pageInfo as {hasNextPage?: unknown; endCursor?: unknown}
  if (typeof pageInfo.hasNextPage !== 'boolean') return false
  if (!(pageInfo.endCursor === null || typeof pageInfo.endCursor === 'string')) return false
  if (!(candidate.nodes == null || Array.isArray(candidate.nodes))) return false

  return true
}

export function topRepositoriesQueryKey(options: {first?: number; hasIssuesEnabled?: boolean; owner?: string}) {
  const {first = 10, hasIssuesEnabled, owner} = options
  return ['item-picker', 'repository-picker-top-repositories', {first, hasIssuesEnabled, owner}] as const
}

async function fetchSearchRepositoriesFromServer(
  searchQuery: string,
  signal?: AbortSignal,
  after?: string,
): Promise<RepositoryPickerSearchRepositoriesServerQueryQuery['search']> {
  const query = new URLSearchParams({
    queryId: RepositoryPickerSearchRepositoriesServerQuery,
    searchQuery,
  })
  if (after) query.set('after', after)

  const response = await reactFetchJSON(`/_ghui/item-pickers/repositories_search_full?${query.toString()}`, {signal})
  if (!response.ok) throw new Error(`Repository full search failed (${response.status})`)

  const json = (await response.json()) as {
    data?: RepositoryPickerSearchRepositoriesServerQueryQuery
    errors?: unknown[]
  }
  if (!isValidSearchConnection(json.data?.search)) {
    throw new Error('Repository full search query returned invalid data')
  }

  return json.data.search
}

const sharedSearchRequests = new SharedRequestManager<RepositoryPickerSearchRepositoriesServerQueryQuery['search']>()

function fetchSharedSearchRepositories(searchQuery: string, signal: AbortSignal) {
  return sharedSearchRequests.fetch(searchQuery, signal, requestSignal =>
    fetchSearchRepositoriesFromServer(searchQuery, requestSignal),
  )
}

export async function fetchTopRepositories(
  options: {
    first?: number
    hasIssuesEnabled?: boolean
    owner?: string
  } = {},
): Promise<RepositoryPickerRepository[]> {
  return getQueryClient().fetchQuery({
    queryKey: topRepositoriesQueryKey(options),
    queryFn: ({signal}) => fetchTopRepositoriesFromServer(options, signal),
  })
}

export async function fetchRepositoriesForOwner({
  owner,
  hasIssuesEnabled,
  first = 10,
}: {
  owner: RepositoryPickerOwner
  hasIssuesEnabled?: boolean
  first?: number
}): Promise<RepositoryPickerRepository[]> {
  const searchQuery = getRepositorySearchQuery('', {owner})
  return getQueryClient().fetchQuery({
    queryKey: ['item-picker', 'repository-picker-owner-repositories', {searchQuery, hasIssuesEnabled, first}],
    queryFn: async ({signal}) => {
      const repositories: RepositoryPickerRepository[] = []
      let after: string | undefined
      let pageCount = 0

      while (repositories.length < first && pageCount < OWNER_REPOSITORY_PRELOAD_MAX_PAGES) {
        let search: RepositoryPickerSearchRepositoriesServerQueryQuery['search']
        try {
          search = await fetchSearchRepositoriesFromServer(searchQuery, signal, after)
        } catch (error) {
          if (isAbortError(error) || repositories.length === 0) throw error
          break
        }
        pageCount += 1

        for (const node of search.nodes ?? []) {
          if (node?.__typename !== 'Repository') continue
          const repository = node as RepositoryPickerRepository
          if (repository.isLocked) continue
          if (hasIssuesEnabled != null && repository.hasIssuesEnabled !== hasIssuesEnabled) continue
          repositories.push(repository)
          if (repositories.length === first) break
        }

        if (!search.pageInfo.hasNextPage || !search.pageInfo.endCursor) break
        after = search.pageInfo.endCursor
      }

      return repositories
    },
  })
}

export async function fetchRepository(owner: string, name: string): Promise<RepositoryPickerRepository | null> {
  return fetchRepositoryFromServerQuery(owner, name)
}

export function repositoryQueryKey(owner: string, name: string) {
  return repositoryQueryKeyFromServer(owner, name)
}

export async function fetchRepositoryFromServer(owner: string, name: string, signal?: AbortSignal) {
  return fetchRepositoryFromServerQuery(owner, name, signal)
}

export function readTopRepositories(viewer: RepositoryPickerTopRepositoriesViewer): RepositoryPickerRepository[] {
  const topRepositories = (viewer.topRepositories.edges || []).flatMap(a => (a?.node ? [a.node] : []))

  return topRepositories
}

export type RepositoryPickerProps = {
  initialRepository: RepositoryPickerRepository | undefined
  topRepositories: RepositoryPickerRepository[] | null
  onSelect: (repo: RepositoryPickerRepository | undefined) => void
  preventDefault?: boolean
  owner?: RepositoryPickerOwner
  organization?: string
  focusRepositoryPicker?: boolean
  enforceAtleastOneSelected?: boolean
  options?: {hasIssuesEnabled?: boolean; readonly?: boolean; includeForks?: boolean}
  renderTrailingVisual?: (repoId: string) => React.ReactElement | undefined
  exclude?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  anchorElement?: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  title?: string
  subtitle?: string | React.ReactElement
  preventClose?: boolean
  triggerOpen?: boolean
  onOpen?: () => void
  onClose?: () => void
  ignoredRepositories?: string[]
  repositoryFilter?: (repo: RepositoryPickerRepository) => boolean
  customNoResultsTitle?: string
  customNoResultsItem?: React.ReactElement
  repoNameOnly?: boolean
  pickerId?: string
  currentRepoVisibility?: RepositoryVisibility
  issueTransferContext?: IssueTransferContext
  // temporary, this will go away when fully ship the primer_react_select_panel_fullscreen_on_narrow FF
  responsiveOnNarrow?: boolean
  notice?: SelectPanelProps['notice']
  hideContainer?: boolean
} & Pick<ItemPickerProps<ExtendedItemProps<RepositoryPickerRepository>>, 'insidePortal'>

export function RepositoryPicker({
  initialRepository,
  onSelect,
  preventDefault,
  owner,
  organization,
  topRepositories,
  focusRepositoryPicker,
  enforceAtleastOneSelected,
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  options: {hasIssuesEnabled, readonly, includeForks} = {
    hasIssuesEnabled: undefined,
    readonly: false,
    includeForks: false,
  },
  renderTrailingVisual,
  exclude,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  anchorElement,
  title,
  subtitle,
  preventClose,
  triggerOpen,
  onOpen,
  onClose,
  ignoredRepositories,
  repositoryFilter,
  customNoResultsItem,
  customNoResultsTitle,
  repoNameOnly,
  pickerId,
  currentRepoVisibility,
  issueTransferContext,
  responsiveOnNarrow,
  notice,
  insidePortal,
  hideContainer,
}: RepositoryPickerProps) {
  const {addToast} = useToastContext()
  const [initiallySelectedRepo, _] = useState<RepositoryPickerRepository | undefined>(initialRepository)
  const [filter, setFilter] = useState('')
  const [searchResults, setSearchResults] = useState<RepositoryPickerRepository[] | undefined>(undefined)
  const [searchLoading, setSearchLoading] = useState<boolean>(topRepositories == null)
  // // We need to keep track of the filter value in a ref because the subscribe function captures the filter value at the time of creation and doesn't update when the state changes.
  const filterRef = useRef(filter)

  // Clear loading after initial data preloaded
  const [previousTopRepositories, setPreviousTopRepositories] = useState(topRepositories)
  if (topRepositories !== previousTopRepositories) {
    setPreviousTopRepositories(topRepositories)
    if (topRepositories != null) {
      setSearchLoading(false)
    }
  }

  const latestSearchRequestRef = useRef(0)
  const searchAbortControllerRef = useRef<AbortController | undefined>(undefined)
  const fetchPossibleTransferRepos = useCallback(
    async (searchQuery: string) => {
      if (!issueTransferContext) return
      if (searchQuery.trim() === '') {
        setSearchResults(undefined)
        setSearchLoading(false)
        return
      }
      setSearchLoading(true)
      try {
        const fetchedRepos = await getQueryClient().fetchQuery({
          queryKey: possibleTransferRepositoriesQueryKey(issueTransferContext, searchQuery),
          queryFn: ({signal}) => fetchPossibleTransferRepositories(issueTransferContext, searchQuery, signal),
        })

        const currentFilter = filterRef.current
        if (searchQuery.trim() === currentFilter.trim()) {
          setSearchResults(fetchedRepos as RepositoryPickerRepository[])
          setSearchLoading(false)
        }
      } catch {
        setSearchLoading(false)
      }
    },
    [issueTransferContext],
  )

  const fetchRepos = useCallback(
    (searchQuery: string, orgs: string[]) => {
      searchAbortControllerRef.current?.abort()
      if (searchQuery.trim() === '') {
        latestSearchRequestRef.current += 1
        setSearchResults(undefined)
        setSearchLoading(false)
        return
      }

      const requestId = latestSearchRequestRef.current + 1
      latestSearchRequestRef.current = requestId
      const controller = new AbortController()
      searchAbortControllerRef.current = controller
      const repositorySearchQuery = getRepositorySearchQuery(searchQuery, {
        owner,
        organization,
        excludeRepo: exclude,
        includeForks,
        repositoryVisibility: currentRepoVisibility,
      })

      setSearchLoading(true)
      void (async () => {
        try {
          const search = await fetchSharedSearchRepositories(repositorySearchQuery, controller.signal)
          let fetchedRepos = (search.nodes || []).flatMap(node =>
            node?.__typename === 'Repository' ? [node as RepositoryPickerRepository] : [],
          )

          fetchedRepos = fetchedRepos.filter(repo => !repo.isLocked && !repo.isArchived)

          if (hasIssuesEnabled) {
            fetchedRepos = fetchedRepos.filter(repo => repo.hasIssuesEnabled === hasIssuesEnabled)
          }

          if (owner) {
            const ownerLogin = owner.login.toLowerCase()
            fetchedRepos = fetchedRepos.filter(repo => repo.owner.login.toLowerCase() === ownerLogin)
          }

          let sortedNextSearchResults = fetchedRepos.sort((a, b) => {
            if (orgs.includes(a.owner.login) && !orgs.includes(b.owner.login)) {
              return -1
            } else if (!orgs.includes(a.owner.login) && orgs.includes(b.owner.login)) {
              return 1
            } else {
              return a.owner.login.localeCompare(b.owner.login)
            }
          })

          if (ignoredRepositories) {
            const ignoredSet = new Set(ignoredRepositories)
            sortedNextSearchResults = sortedNextSearchResults.filter(repo => !ignoredSet.has(repo.nameWithOwner))
          }
          if (repositoryFilter) {
            sortedNextSearchResults = sortedNextSearchResults.filter(repositoryFilter)
          }
          const currentFilter = filterRef.current
          if (requestId === latestSearchRequestRef.current && searchQuery.trim() === currentFilter.trim()) {
            setSearchResults(sortedNextSearchResults)
            setSearchLoading(false)
          }
        } catch (error) {
          if (requestId !== latestSearchRequestRef.current) return
          setSearchLoading(false)
          if (isAbortError(error)) return
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotSearchRepositories,
          })
        } finally {
          if (searchAbortControllerRef.current === controller) searchAbortControllerRef.current = undefined
        }
      })()
    },
    [
      owner,
      organization,
      exclude,
      includeForks,
      currentRepoVisibility,
      hasIssuesEnabled,
      ignoredRepositories,
      repositoryFilter,
      addToast,
    ],
  )

  useEffect(() => {
    return () => {
      latestSearchRequestRef.current += 1
      searchAbortControllerRef.current?.abort()
    }
  }, [])

  const fetchedRepos = useMemo(() => {
    let nodes = topRepositories || []

    if (initiallySelectedRepo) {
      if (!nodes.find(node => node.id === initiallySelectedRepo.id)) {
        nodes = [initiallySelectedRepo, ...nodes]
      }
    }

    if (initialRepository) {
      if (!nodes.find(node => node.id === initialRepository.id)) {
        nodes = [initialRepository, ...nodes]
      }
    }

    if (owner) {
      const ownerLogin = owner.login.toLowerCase()
      nodes = nodes.filter(repo => repo.owner.login.toLowerCase() === ownerLogin)
    } else if (organization) {
      const organizationLogin = organization.toLowerCase()
      nodes = nodes.filter(repo => repo.owner.login.toLowerCase() === organizationLogin)
    }

    if (exclude) {
      nodes = nodes.filter(repo => repo.nameWithOwner !== exclude)
    }

    if (ignoredRepositories) {
      const ignoredSet = new Set(ignoredRepositories)
      nodes = nodes.filter(repo => !ignoredSet.has(repo.nameWithOwner))
    }

    return nodes.slice(0, 10)
  }, [topRepositories, initiallySelectedRepo, initialRepository, owner, organization, exclude, ignoredRepositories])

  const knownOrgs = useMemo(() => [...new Set(fetchedRepos.map(repo => repo.owner.login))], [fetchedRepos])

  const debounceFetchRepo = useDebounce((nextValue: string) => {
    if (issueTransferContext) {
      fetchPossibleTransferRepos(nextValue)
    } else {
      fetchRepos(nextValue, knownOrgs)
    }
  }, VALUES.pickerDebounceTime)

  const getItemKey = useCallback((o: RepositoryPickerRepository) => o.id, [])

  const repositoryPickerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (focusRepositoryPicker) repositoryPickerRef.current?.focus()
  }, [repositoryPickerRef, focusRepositoryPicker])

  const renderAnchor = useCallback(
    ({...anchorProps}: React.HTMLAttributes<HTMLElement>) => {
      if (anchorElement) {
        return anchorElement(anchorProps)
      }

      const repositoryValue = initialRepository
        ? repoNameOnly
          ? initialRepository.name
          : initialRepository.nameWithOwner
        : 'No repository selected'

      const label = initialRepository ? `Selected repository: ${repositoryValue}` : LABELS.selectRepository

      return (
        <Button
          leadingVisual={RepoIcon}
          trailingVisual={TriangleDownIcon}
          aria-label={label}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          {...anchorProps}
          disabled={readonly}
          ref={repositoryPickerRef}
        >
          {initialRepository ? (
            <span>
              {repoNameOnly ? initialRepository.name : `${initialRepository.owner.login}/${initialRepository.name}`}
            </span>
          ) : (
            LABELS.selectRepository
          )}
        </Button>
      )
    },
    [anchorElement, ariaDescribedBy, ariaLabelledBy, initialRepository, readonly, repoNameOnly],
  )

  const convertToItemProps = useCallback(
    (repo: RepositoryPickerRepository): ExtendedItemProps<RepositoryPickerRepository> => ({
      // this is a hack to make sure that we are using the prop
      id: `${repo.id}_${repo.databaseId}_${repo.slashCommandsEnabled}`,
      text: repoNameOnly ? repo.name : `${repo.owner.login}/${repo.name}`,
      source: repo,
      leadingVisual: () => (repo.isPrivate ? <LockIcon size={12} /> : <RepoIcon size={12} />),
      trailingVisual: renderTrailingVisual?.(repo.id),
      className: styles.Item,
    }),
    [renderTrailingVisual, repoNameOnly],
  )

  const items = useMemo(() => {
    if (searchResults) return searchResults
    const filteredItems = fetchedRepos.filter(repo => !repo.isArchived && !repo.isLocked)
    if (repositoryFilter) {
      return filteredItems.filter(repositoryFilter)
    }
    return filteredItems
  }, [fetchedRepos, repositoryFilter, searchResults])

  const autoSelectFirstRepo = useEffectEvent(() => {
    if (!initialRepository && items.length > 0 && !preventDefault) {
      onSelect(items[0])
    }
  })

  useEffect(() => {
    // automatically select the first repo
    autoSelectFirstRepo()
  }, [])

  const filterItems = useCallback(
    (value: string) => {
      if (readonly) return
      const trimmedValue = value.trim()
      // Only run search if the value has changed
      if (filter === trimmedValue) return
      debounceFetchRepo(trimmedValue)
      setFilter(trimmedValue)
      filterRef.current = trimmedValue
    },
    [readonly, filter, debounceFetchRepo],
  )

  const showNoMatchMessage = useMemo(() => items.length === 0, [items.length])
  const message = useMemo<SelectPanelProps['message'] | undefined>(
    () =>
      showNoMatchMessage
        ? {
            title: customNoResultsTitle ?? 'No repositories match',
            body: customNoResultsItem ?? 'Try searching with a different query for results.',
            variant: 'empty',
          }
        : undefined,
    [showNoMatchMessage, customNoResultsTitle, customNoResultsItem],
  )

  return (
    <div
      className={clsx(styles.repositoryPickerContainer, hideContainer && styles.hideContainer)}
      data-testid="repository-picker-container"
    >
      <ItemPicker
        items={items}
        initialSelectedItems={initialRepository ? [initialRepository] : []}
        filterItems={filterItems}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={LABELS.selectRepository}
        selectionVariant="single"
        onSelectionChange={([firstRepo]) => {
          return onSelect(firstRepo)
        }}
        loading={searchLoading}
        renderAnchor={renderAnchor}
        selectPanelRef={repositoryPickerRef}
        enforceAtleastOneSelected={enforceAtleastOneSelected}
        resultListAriaLabel={'Repository results'}
        height={'large'}
        width={'medium'}
        title={title}
        subtitle={subtitle}
        preventClose={preventClose}
        triggerOpen={triggerOpen}
        onOpen={onOpen}
        onClose={onClose}
        pickerId={pickerId}
        improvedNoMatchAccessibility={showNoMatchMessage}
        noMatchMessage={message}
        notice={notice}
        responsiveOnNarrow={responsiveOnNarrow}
        insidePortal={insidePortal}
      />
    </div>
  )
}
